// Submit-timeout recovery for the apply flow.
//
// The 2026-06-10 incident: POST /apply/complete exceeded the client's 20s
// fetch budget, the candidate saw "Request timed out" — but the server kept
// executing and the application committed. A false failure with no recovery:
// the candidate left believing they hadn't applied.
//
// After a timeout/network failure, the frontend now polls the candidate's own
// applications list (GET /api/candidates/applications — the gateway
// invalidates this cache when complete finishes) looking for an application
// on this job. Found → the submit actually succeeded and the flow can proceed
// to the success page. Injectable fetcher/sleep keep this unit-testable.

export interface ProbeApplication {
  id: string;
  jobId: string;
}

export interface ProbeResult {
  applicationId: string;
}

const DEFAULT_ATTEMPTS = 6;
const DEFAULT_DELAY_MS = 5_000;

/** Errors where the request outcome is UNKNOWN (worth probing), as opposed
 *  to a definitive server rejection (validation error, expired session). */
export function isIndeterminateSubmitError(error: string | undefined | null): boolean {
  if (!error) return false;
  const e = error.toLowerCase();
  return (
    e.includes('timed out') ||
    e.includes('timeout') ||
    e.includes('network') ||
    e.includes('failed to fetch') ||
    e.includes('load failed')
  );
}

/** A duplicate-application rejection means a previous submit DID land —
 *  surface it as success, not as an error. */
export function isAlreadyAppliedError(error: string | undefined | null): boolean {
  if (!error) return false;
  return error.toLowerCase().includes('already applied');
}

/**
 * Poll the candidate's applications for one matching this job.
 *
 * @param jobIdentifiers every id the application row might carry for this job
 *        (route slug + job UUID) — backend rows store whichever the session had.
 */
export async function probeForSubmittedApplication(opts: {
  jobIdentifiers: Array<string | undefined | null>;
  fetchApplications: () => Promise<{ success: boolean; data?: ProbeApplication[] | null }>;
  attempts?: number;
  delayMs?: number;
  sleep?: (ms: number) => Promise<void>;
}): Promise<ProbeResult | null> {
  const ids = new Set(
    opts.jobIdentifiers.filter((v): v is string => typeof v === 'string' && v.length > 0),
  );
  if (ids.size === 0) return null;

  const attempts = opts.attempts ?? DEFAULT_ATTEMPTS;
  const delayMs = opts.delayMs ?? DEFAULT_DELAY_MS;
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));

  for (let i = 0; i < attempts; i++) {
    // Delay FIRST: at t=0 the server is almost certainly still mid-flight —
    // the incident request finished ~23s after the client gave up.
    await sleep(delayMs);
    try {
      const result = await opts.fetchApplications();
      if (result.success && Array.isArray(result.data)) {
        const hit = result.data.find((app) => app && ids.has(app.jobId));
        if (hit) return { applicationId: hit.id };
      }
    } catch {
      // Transient list failure — keep polling; the submit outcome is still unknown.
    }
  }
  return null;
}
